import { useCallback, useEffect, useRef, useState } from "react";

// Hauteur disponible entre le haut d'un élément et le bas de la fenêtre.
// - targetSelector : élément mesuré, cherché dans le conteneur (ex. corps d'une table antd) ; le conteneur lui-même sinon
// - bottomOffset : espace à réserver sous le conteneur (padding/bordures des conteneurs parents)
// Ce qui suit l'élément dans le conteneur (ex. pagination d'une table) est mesuré et réservé automatiquement.
// Sa marge basse n'est comptée que si elle ne déborde pas du conteneur (ex. conteneur flex item, cf. .view-spin) :
// sinon elle s'ajoute à la hauteur et la vue défile d'autant.
// Les onglets restent montés en display: none : la mesure est ignorée tant que le conteneur n'est pas rendu,
// et le ResizeObserver la relance quand il redevient visible (ou quand son contenu apparaît).
const useFillHeight = ({ enabled = true, targetSelector, bottomOffset = 0, minHeight = 200 } = {}) => {
  const containerRef = useRef(null);
  const [height, setHeight] = useState(null);

  const updateHeight = useCallback(() => {
    const container = containerRef.current;
    if (!container || container.getClientRects().length === 0) return;

    const target = (targetSelector && container.querySelector(targetSelector)) || container;
    const targetRect = target.getBoundingClientRect();
    const below = container.getBoundingClientRect().bottom - targetRect.bottom;
    const available = Math.floor(window.innerHeight - targetRect.top - below - bottomOffset);
    setHeight(Math.max(available, minHeight));
  }, [targetSelector, bottomOffset, minHeight]);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    updateHeight();

    // Mise à jour différée d'une frame : le re-render redimensionne le conteneur observé,
    // ce qui déclencherait sinon l'erreur « ResizeObserver loop completed with undelivered notifications »
    let frame = null;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateHeight);
    });
    observer.observe(containerRef.current);
    window.addEventListener("resize", updateHeight);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [enabled, updateHeight]);

  return { containerRef, height: enabled ? height : null };
};

export default useFillHeight;
